import React, { useState, useEffect, useRef } from 'react';
import '../styles/components/general-splash-v2.css';

const SPLASH_MIN_DURATION_MS = 3000;
const SPLASH_MAX_DURATION_MS = 5000;

const dispatchSplashState = (active) => {
  window.__MPT_SPLASH_ACTIVE = active;
  window.dispatchEvent(new window.CustomEvent('mpt:splash-state', {
    detail: { active, source: 'general' }
  }));
};

// eslint-disable-next-line no-unused-vars
const GeneralSplashScreen = ({ onComplete, isDarkMode: _isDarkMode = true }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentPark, setCurrentPark] = useState(0);
  const canvasRef = useRef(null);
  const updatePendingRef = useRef(window.__MPT_SW_UPDATE_PENDING_DURING_SPLASH === true);

  useEffect(() => {
    dispatchSplashState(true);

    return () => {
      dispatchSplashState(false);
    };
  }, []);

  // Liste des parcs pour l'animation de texte
  const parks = [
    'Disneyland Paris',
    'Disney Adventure World',
    'Europa-Park',
    'PortAventura',
    'Universal Studios',
    'Magic Kingdom',
    'Parc Astérix',
    'Phantasialand',
    'Efteling',
    'Alton Towers'
  ];

  // Animation des particules sur le canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Créer les particules
    function Particle() {
      this.reset();
    }

    Particle.prototype.reset = function () {
      this.x = Math.random() * canvas.width;
      this.y = Math.random() * canvas.height;
      this.size = Math.random() * 3 + 1;
      this.speedX = (Math.random() - 0.5) * 0.5;
      this.speedY = (Math.random() - 0.5) * 0.5;
      this.opacity = Math.random() * 0.5 + 0.2;
      // Couleurs bleu nuit / violet néon
      const colors = [
        'rgba(138, 43, 226, ', // Violet
        'rgba(75, 0, 130, ',   // Indigo
        'rgba(0, 191, 255, ',  // Cyan
        'rgba(147, 112, 219, ', // Violet clair
        'rgba(255, 0, 255, '   // Magenta
      ];
      this.color = colors[Math.floor(Math.random() * colors.length)];
    };

    Particle.prototype.update = function () {
      this.x += this.speedX;
      this.y += this.speedY;

      if (this.x < 0 || this.x > canvas.width) this.speedX *= -1;
      if (this.y < 0 || this.y > canvas.height) this.speedY *= -1;
    };

    Particle.prototype.draw = function () {
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      ctx.fillStyle = this.color + this.opacity + ')';
      ctx.shadowBlur = 15;
      ctx.shadowColor = this.color + '1)';
      ctx.fill();
    };

    // Initialiser les particules
    for (let i = 0; i < 80; i++) {
      particles.push(new Particle());
    }

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Dessiner les connexions entre particules proches
      particles.forEach((p1, i) => {
        particles.slice(i + 1).forEach(p2 => {
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 120) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(138, 43, 226, ${0.15 * (1 - distance / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        });
      });

      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationId);
    };
  }, []);

  // Animation de progression avec durée dynamique:
  // 3s en temps normal, jusqu'à 5s si une mise à jour SW est en cours pendant le splash.
  useEffect(() => {
    const startTime = Date.now();
    let animationFrameId;
    let finishTimer = null;
    let isFinishing = false;

    const finishSplash = () => {
      if (isFinishing) return;
      isFinishing = true;
      setProgress(100);
      dispatchSplashState(false);
      setIsVisible(false);
      setTimeout(onComplete, 500);
    };

    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const targetDuration = updatePendingRef.current ? SPLASH_MAX_DURATION_MS : SPLASH_MIN_DURATION_MS;
      const nextProgress = Math.min(99, Math.round((elapsed / targetDuration) * 100));

      setProgress(nextProgress);
      if (!isFinishing) {
        animationFrameId = requestAnimationFrame(updateProgress);
      }
    };

    updateProgress();

    const scheduleFinish = () => {
      if (finishTimer) {
        clearTimeout(finishTimer);
      }

      const elapsed = Date.now() - startTime;
      const targetDuration = updatePendingRef.current ? SPLASH_MAX_DURATION_MS : SPLASH_MIN_DURATION_MS;
      finishTimer = setTimeout(finishSplash, Math.max(0, targetDuration - elapsed));
    };

    const handleSwUpdateDuringSplash = (event) => {
      updatePendingRef.current = event.detail?.pending === true;
      scheduleFinish();
    };

    window.addEventListener('mpt:sw-update-during-splash', handleSwUpdateDuringSplash);
    scheduleFinish();

    // Rotation des noms de parcs
    const parkInterval = setInterval(() => {
      setCurrentPark(prev => (prev + 1) % parks.length);
    }, 400);

    return () => {
      cancelAnimationFrame(animationFrameId);
      if (finishTimer) {
        clearTimeout(finishTimer);
      }
      clearInterval(parkInterval);
      window.removeEventListener('mpt:sw-update-during-splash', handleSwUpdateDuringSplash);
    };
  }, [onComplete, parks.length]);

  if (!isVisible) return null;

  return (
    <div className={`splash-v2 ${!isVisible ? 'fade-out' : ''}`}>
      {/* Canvas pour les particules */}
      <canvas ref={canvasRef} className="splash-v2-canvas" />

      {/* Overlay gradient */}
      <div className="splash-v2-overlay" />

      {/* Contenu principal */}
      <div className="splash-v2-content">
        {/* Logo fixe */}
        <div className="splash-v2-logo-container">
          <div className="splash-v2-logo-glow" />
          <picture>
            <source
              srcSet="/images/webp/MPT_Logo2_BLANC.webp"
              type="image/webp"
            />
            <img
              src="/images/MPT_Logo2_BLANC.png"
              alt="Magic Park Times"
              className="splash-v2-logo"
              width="400"
              height="182"
            />
          </picture>
        </div>

        {/* Loader circulaire néon */}
        <div className="splash-v2-loader">
          <svg className="splash-v2-loader-svg" viewBox="0 0 100 100">
            <defs>
              <linearGradient id="neonGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#8a2be2" />
                <stop offset="25%" stopColor="#00bfff" />
                <stop offset="50%" stopColor="#ff00ff" />
                <stop offset="75%" stopColor="#4b0082" />
                <stop offset="100%" stopColor="#8a2be2" />
              </linearGradient>
              <filter id="neonGlow">
                <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            {/* Cercle de fond */}
            <circle
              className="splash-v2-loader-bg"
              cx="50"
              cy="50"
              r="45"
              fill="none"
              strokeWidth="2"
            />
            {/* Cercle de progression */}
            <circle
              className="splash-v2-loader-progress"
              cx="50"
              cy="50"
              r="45"
              fill="none"
              strokeWidth="3"
              strokeDasharray={`${progress * 2.83} 283`}
              filter="url(#neonGlow)"
            />
            {/* Points lumineux */}
            <circle className="splash-v2-dot dot-1" cx="50" cy="5" r="3" />
            <circle className="splash-v2-dot dot-2" cx="95" cy="50" r="3" />
            <circle className="splash-v2-dot dot-3" cx="50" cy="95" r="3" />
            <circle className="splash-v2-dot dot-4" cx="5" cy="50" r="3" />
          </svg>
          <div className="splash-v2-loader-text">
            <span className="splash-v2-percentage">{progress}%</span>
          </div>
        </div>

        {/* Texte animé des parcs */}
        <div className="splash-v2-parks">
          <span className="splash-v2-parks-label">Chargement de</span>
          <span className="splash-v2-park-name" key={currentPark}>
            {parks[currentPark]}
          </span>
        </div>

        {/* Statistiques */}
        <div className="splash-v2-stats">
          <div className="splash-v2-stat">
            <span className="splash-v2-stat-number">44</span>
            <span className="splash-v2-stat-label">Parcs</span>
          </div>
          <div className="splash-v2-stat-divider" />
          <div className="splash-v2-stat">
            <span className="splash-v2-stat-number">24</span>
            <span className="splash-v2-stat-label">Europe</span>
          </div>
          <div className="splash-v2-stat-divider" />
          <div className="splash-v2-stat">
            <span className="splash-v2-stat-number">15</span>
            <span className="splash-v2-stat-label">Amérique</span>
          </div>
          <div className="splash-v2-stat-divider" />
          <div className="splash-v2-stat">
            <span className="splash-v2-stat-number">5</span>
            <span className="splash-v2-stat-label">Asie</span>
          </div>
        </div>

        {/* Message de bienvenue */}
        <div className="splash-v2-welcome">
          <h1>Bienvenue sur Magic Park Times</h1>
          <p>Temps d&apos;attente en temps réel pour les parcs à thème</p>
        </div>
      </div>

      {/* Effet de scan horizontal */}
      <div className="splash-v2-scanline" />
    </div>
  );
};

export default GeneralSplashScreen;
